import React, { useState, useEffect, useCallback } from 'react';
import { GoogleMap, LoadScript, Marker, InfoWindow } from '@react-google-maps/api';
import { useLocation } from 'react-router-dom';  
import { AiFillPhone, AiOutlineFieldTime } from 'react-icons/ai';
import { MdPlace } from "react-icons/md";
import { HiMail } from "react-icons/hi";
import { getCompaniesLibraries } from 'api/company';
import { MAP_KEY } from 'constants';

const Map = (props) => {
  const [selectedItem, setSelectedItem] = useState(null);
  const [zoom, setZoom] = useState(6);
  const [librariesData, setLibrariesData] = useState([]);
  const [userLocation, setUserLocation] = useState(null);
  const [highlightedLibrary, setHighlightedLibrary] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [mapHeight, setMapHeight] = useState(window.innerWidth < 992 ? '100vh' : '120vh');
  const location = useLocation(); 

  useEffect(() => {
    const handleResize = () => {
      setMapHeight(window.innerWidth < 992 ? '100vh' : '120vh');
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const mapOptions = {
    minZoom: 3,
    restriction: {
      latLngBounds: {
        north: 85,
        south: -85,
        west: -179.9999,
        east: 179.9999,
      },
      strictBounds: true,
    },
    gestureHandling: 'cooperative',
    zoomControl: true,
    mapTypeControl: true,
    scaleControl: true,
    streetViewControl: true,
    rotateControl: true,
    fullscreenControl: true
  };

  const mapContainerStyle = {
    width: '100%',
    height: mapHeight,
    position: 'relative',
    margin: 0,
    padding: 0
  };

  const onLoad = useCallback(() => {
    setIsLoaded(true);
  }, []);

  async function getLibrariesAsoc(by_city_or_locality = '', name = '', service_ids = []) {
    try {
      const servicesIdsString = service_ids.join(',');
      const params = new URLSearchParams({
        by_city_or_locality,
        name,
        service_ids: servicesIdsString,
      });
      const rs = await getCompaniesLibraries(params);
      if (rs.status === 200) {
        return rs.data;
      }
      throw new Error('Error al obtener los datos de la API');
    } catch (error) {
      console.error('Error al obtener los datos de la API:', error);
      return null;
    }
  }

  useEffect(() => {
    getLibrariesAsoc();
  }, []);

  useEffect(() => {
    const filterLibraries = async () => {
      const response = await getLibrariesAsoc(props.by_city_or_locality, props.name, props.service_ids);
      if (response && response.results.length > 0) {
        const library = response.results[0];
        setLibrariesData(response.results);

        if (props.callByState) {
          if (props.by_city_or_locality !== '' || props.name !== '' || props.service_ids?.length) {
            setZoom(20);
            const lat = parseFloat(library.latitude);
            const lng = parseFloat(library.longitude);
            props.setMapCenter({ lat, lng });
          } else {
            setZoom(6)
          }
          props.setCallByState(false);
        }
      } else {
        props.showToastInfo("sin resultados");
      }
    };

    filterLibraries();
  }, [props.callByState]);

  useEffect(() => {
    if (props.latitude && props.longitude) {
      const lat = Number(props.latitude);
      const lng = Number(props.longitude);

      if (!isNaN(lat) && !isNaN(lng)) {
        const newCenter = { lat, lng };
        props.setMapCenter(newCenter);
        setUserLocation(newCenter);
        setZoom(15);
      }
    }
  }, [props.latitude, props.longitude]);

  useEffect(() => {
    const selectedId = location.state?.selectedLibraryId || location.state?.selectedLibrary;
    
    if (selectedId) {
      setHighlightedLibrary(selectedId.toString());
      
      if (location.state?.selectedCoords) {
        const [lat, lng] = location.state.selectedCoords;
        const newCenter = {
          lat: parseFloat(lat),
          lng: parseFloat(lng)
        };
        
        props.setMapCenter(newCenter);
        setZoom(15);
        
        const selectedLibrary = librariesData.find(lib => lib.id.toString() === selectedId.toString());
        if (selectedLibrary) {
          setSelectedItem(selectedLibrary);
        } else {
          const fetchSelectedLibrary = async () => {
            const response = await getLibrariesAsoc('', '', []);
            if (response && response.results.length > 0) {
              setLibrariesData(response.results);
              
              const library = response.results.find(lib => lib.id.toString() === selectedId.toString());
              if (library) {
                setSelectedItem(library);
              }
            }
          };
          
          fetchSelectedLibrary();
        }
      }
    }
  }, [location.state, props.setMapCenter, librariesData]);

  const getMarkerIcon = (type, libraryId = null) => {
    if (!isLoaded) return null;

    if (type === 'user') {
      return {
        url: 'https://maps.google.com/mapfiles/ms/icons/blue-dot.png',
        scaledSize: new window.google.maps.Size(50, 50)
      };
    }
    if (highlightedLibrary && libraryId && libraryId.toString() === highlightedLibrary.toString()) {
      return {
        url: 'https://maps.google.com/mapfiles/ms/icons/green-dot.png',
        scaledSize: new window.google.maps.Size(60, 60)
      };
    }
    return {
      url: 'https://maps.google.com/mapfiles/ms/icons/orange-dot.png',
      scaledSize: new window.google.maps.Size(50, 50)
    };
  };

  return (
    <div className="container-googlemap">
      <LoadScript googleMapsApiKey={MAP_KEY} onLoad={onLoad}>
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={props.mapCenter}
          zoom={zoom}
          options={mapOptions}
        >
          {isLoaded && userLocation && (
            <Marker
              position={userLocation}
              icon={getMarkerIcon('user')}
              title="Tu ubicación"
            />
          )}

          {isLoaded && librariesData?.map((bookstore, val) => (
            <Marker
              key={val}
              position={{
                lat: parseFloat(bookstore.latitude),
                lng: parseFloat(bookstore.longitude),
              }}
              icon={getMarkerIcon('library',bookstore.id)}
              onClick={() => setSelectedItem(bookstore)}
            />
          ))}

          {selectedItem && (
            <InfoWindow
              position={{
                lat: parseFloat(selectedItem.latitude),
                lng: parseFloat(selectedItem.longitude),
              }}
              onCloseClick={() => setSelectedItem(null)}
            >
              <div className="d-flex flex-column">
                <h5 className="info-map-name aside-title">{selectedItem.name}</h5>
                <div>
                  {selectedItem.logo && (
                    <div className="container-info-map align-items-start gap-3 pb-3">
                      <img className="mb-0" src={selectedItem.logo} width={100} alt={selectedItem.name} />
                      <p>{selectedItem.descripcion_pub}</p>
                    </div>
                  )}
                  {(selectedItem.telefono || selectedItem.direccion || selectedItem.email || selectedItem.business_hours) && (
                    <div className="d-flex flex-wrap container-info-map gap-3">
                      {selectedItem.telefono && (
                        <div className='d-flex p-2 align-items-center'>
                          <AiFillPhone className="icon-info-map" />
                          <p className="mb-0 with-margin">{selectedItem.telefono}</p>
                        </div>
                      )}
                      {selectedItem.direccion && (
                        <div className='d-flex p-2 align-items-center'>
                          <MdPlace className="icon-info-map" />
                          <p className="mb-0">{selectedItem.direccion}</p>
                        </div>
                      )}
                      {selectedItem.email && (
                        <div className='d-flex p-2 align-items-center'>
                          <HiMail className="icon-info-map" />
                          <p className="mb-0">{selectedItem.email}</p>
                        </div>
                      )}
                      {selectedItem.business_hours && (
                        <div className='d-flex p-2 align-items-center'>
                          <AiOutlineFieldTime className="icon-info-map" />
                          <p className="mb-0">{selectedItem.business_hours}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </InfoWindow>
          )}
        </GoogleMap>
      </LoadScript>
    </div>
  );
};

export default Map;

